<template>
  <div v-if="shouldShow" class="bg-teal-50 rounded-lg p-3 flex flex-col gap-2">
    <div class="flex gap-2">
      <component
        :is="currentIcon"
        class="w-4 h-4 text-teal-600 flex-shrink-0"
      />
      <div class="text-xs text-stone-700">
        <template v-if="ticketsToNextTier > 0">
          <span class="!text-teal-900">{{ moreTicketsMessage + ' ' }}</span>
          <span class="font-normal !text-teal-900">{{ toUnlockMessage }}</span>
        </template>
        <span v-else class="!text-teal-900">{{ tierUnlockedMessage }}</span>

        <span
          v-if="showPrice"
          class="font-semibold text-teal-600 whitespace-nowrap"
        >
          {{ ' ' + nextTierPrice }}
        </span>
      </div>
    </div>
    <div class="relative h-2 bg-teal-100 rounded-full overflow-hidden">
      <div
        class="absolute top-0 left-0 h-full bg-teal-600 rounded-full transition-all duration-300"
        :style="{ width: `${progressPercentage}%` }"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  EventDate,
  EventDescription,
  PricingCategory,
  PricingScale,
  Pricings,
} from '~/composables/types/event'
import { eventDateGetters } from '~/utils/eventDateGetters'
import { eventGetters } from '~/utils/eventGetters'
import { formatMoney } from '~/utils/currencyHelpers'
import { calculatePromotionDiscount } from '~/utils/pricingGetters'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import IconTags from '~/assets/icons/tags.svg?component'
import PartyPopper from '~/assets/icons/confetti.svg?component'

interface Props {
  selectedDate: EventDate | undefined
  selectedCategoryQuantities?: Record<string, number>
  selectedTicketQuantity?: number
  pricingData: Pricings | undefined
  isPrivate: boolean
  loadingPricings: boolean
  eventData: EventDescription
}

const props = defineProps<Props>()

const { t, locale } = useI18n()

// Find the category with tiers (prefer ADULT, fallback to first with tiers)
const categoryWithTiers = computed<PricingCategory | undefined>(() => {
  const categories =
    props.eventData?.pricing?.data?.pricingCategories?.data || []

  // First try to find ADULT category with tiers
  const adultCategory = categories.find(
    (cat) =>
      cat.ticket_category === 'ADULT' &&
      eventGetters.categoryHasTieredPricing(cat)
  )

  if (adultCategory) return adultCategory

  // Fallback to first category with multiple tiers
  return categories.find((cat) => eventGetters.categoryHasTieredPricing(cat))
})

// Get pricing scales for the category
const pricingScales = computed<PricingScale[]>(() => {
  return categoryWithTiers.value?.pricingScales?.data || []
})

// Check if we have multiple tiers
const hasMultipleTiers = computed(() => {
  return pricingScales.value.length > 1
})

// Check if we have multiple categories (more than 1)
const hasMultipleCategories = computed(() => {
  const categories =
    props.eventData?.pricing?.data?.pricingCategories?.data || []
  return categories.length > 1
})

// Get the category ticket quantity
// Use selectedCategoryQuantities for multiple categories, selectedTicketQuantity for single category
const categoryTicketQuantity = computed(() => {
  if (!categoryWithTiers.value) return 0

  const categoryType = categoryWithTiers.value.ticket_category

  // Multiple categories: use selectedCategoryQuantities
  if (hasMultipleCategories.value) {
    if (
      props.selectedCategoryQuantities &&
      Object.keys(props.selectedCategoryQuantities).length > 0
    ) {
      return props.selectedCategoryQuantities[categoryType] || 0
    }
    return 0
  }

  // Single category: use selectedTicketQuantity
  if (props.selectedTicketQuantity !== undefined) {
    return props.selectedTicketQuantity
  }

  return 0
})

// Get current tier based on quantity
const currentTier = computed<PricingScale | undefined>(() => {
  if (!hasMultipleTiers.value) return undefined

  const quantity = categoryTicketQuantity.value
  const scales = pricingScales.value

  // Sort scales by min_participants
  const sortedScales = [...scales].sort(
    (a, b) => a.min_participants - b.min_participants
  )

  // Find the tier that matches current quantity
  for (let i = sortedScales.length - 1; i >= 0; i--) {
    const scale = sortedScales[i]
    if (quantity >= scale.min_participants) {
      return scale
    }
  }

  // If quantity is below all tiers, return first tier
  return sortedScales[0]
})

// Get next tier (tier with higher min_participants)
const nextTier = computed<PricingScale | undefined>(() => {
  if (!hasMultipleTiers.value || !currentTier.value) return undefined

  const scales = [...pricingScales.value].sort(
    (a, b) => a.min_participants - b.min_participants
  )

  const currentIndex = scales.findIndex((s) => s.id === currentTier.value!.id)

  // If we're at the last tier, return undefined (no next tier)
  if (currentIndex === scales.length - 1) return undefined

  return scales[currentIndex + 1]
})

// Check if we're at the maximum tier
const isMaxTier = computed(() => {
  if (!hasMultipleTiers.value || !currentTier.value) return false

  const scales = [...pricingScales.value].sort(
    (a, b) => a.min_participants - b.min_participants
  )

  const lastTier = scales[scales.length - 1]
  return currentTier.value.id === lastTier.id
})

// Calculate progress percentage
const progressPercentage = computed(() => {
  if (!hasMultipleTiers.value || !currentTier.value) return 0

  const quantity = categoryTicketQuantity.value

  // If we're at max tier and reached it, show 100%
  if (isMaxTier.value && quantity >= currentTier.value.min_participants) {
    return 100
  }

  // If no next tier, show 100%
  if (!nextTier.value) {
    return 100
  }

  // Calculate progress from current tier to next tier
  const currentTierMin = currentTier.value.min_participants
  const nextTierMin = nextTier.value.min_participants

  // For private events, use privateMinTickets as baseline if applicable
  let baseline = currentTierMin
  if (props.isPrivate && props.selectedDate) {
    const privateMin = eventDateGetters.getPrivateMinTickets(props.selectedDate)
    // Only use privateMin if it's higher than current tier min
    if (privateMin > currentTierMin) {
      baseline = privateMin
    }
  }

  // Calculate progress
  const progress = ((quantity - baseline) / (nextTierMin - baseline)) * 100

  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, progress))
})

// Calculate tickets needed to reach next tier
const ticketsToNextTier = computed(() => {
  if (!nextTier.value || !currentTier.value) return 0

  const quantity = categoryTicketQuantity.value
  const nextTierMin = nextTier.value.min_participants

  return Math.max(0, nextTierMin - quantity)
})

// Get category label for translation
const categoryLabel = computed(() => {
  if (!categoryWithTiers.value) return 'tickets'

  const categoryType = categoryWithTiers.value.ticket_category
  return t(`product.show.checkout.category.${categoryType}`)
})

// More tickets message (semibold)
const moreTicketsMessage = computed(() => {
  if (ticketsToNextTier.value > 0) {
    // Vue i18n pluralization: pass count as second argument, use {n} in translations
    // TypeScript workaround: Vue i18n types don't properly infer pluralization overload with named params
    return (t as any)(
      'product.show.checkout.tieredPricing.moreTickets',
      ticketsToNextTier.value,
      {
        category: categoryLabel.value,
      }
    )
  }
  return ''
})

// To unlock message (normal)
const toUnlockMessage = computed(() => {
  if (ticketsToNextTier.value > 0) {
    return t('product.show.checkout.tieredPricing.toUnlock')
  }
  return ''
})

// Tier unlocked message
const tierUnlockedMessage = computed(() => {
  return t('product.show.checkout.tieredPricing.tierUnlocked')
})

// Get next tier price (with promotion discount applied when available)
const nextTierPrice = computed(() => {
  const getFormattedPrice = (retailPrice: string) => {
    const priceAmount = parseFloat(retailPrice)
    if (isNaN(priceAmount)) return ''

    const promotion = props.eventData?.activePromotion?.data
    const discountedAmount = calculatePromotionDiscount(priceAmount, promotion)
    const finalPriceAmount =
      discountedAmount !== undefined ? discountedAmount : priceAmount

    return formatMoney(
      finalPriceAmount,
      convertLocaleIntoBCP47(locale.value),
      'EUR',
      2
    )
  }

  if (!nextTier.value) {
    // If no next tier, use current tier price
    if (currentTier.value?.price?.data?.retail_price) {
      const formatted = getFormattedPrice(
        currentTier.value.price.data.retail_price
      )
      return formatted || ''
    }
    return ''
  }

  // Format next tier price
  if (nextTier.value.price?.data?.retail_price) {
    const formatted = getFormattedPrice(nextTier.value.price.data.retail_price)
    if (formatted) {
      return `${formatted}${t('product.show.checkout.tieredPricing.pricePerTicket')}`
    }
  }

  return ''
})

// Show price only when at least 1 ticket is selected
const showPrice = computed(() => {
  return categoryTicketQuantity.value > 0 && nextTierPrice.value !== ''
})

// Icon to display
const currentIcon = computed(() => {
  if (
    isMaxTier.value &&
    categoryTicketQuantity.value >= currentTier.value!.min_participants
  ) {
    return PartyPopper
  }
  return IconTags
})

// Component visibility
const shouldShow = computed(() => {
  // Hide if no tiers available
  if (!hasMultipleTiers.value) return false

  // Hide if no date selected
  if (!props.selectedDate) return false

  // Hide if loading and no quantity selected yet
  if (props.loadingPricings && categoryTicketQuantity.value === 0) {
    return false
  }

  return true
})
</script>
